import type { StoreApi } from 'zustand';
import { fetchCurrentUser } from '@/utils/supabase/current-user';

export interface CurrentUser {
  id: string;
  email: string;
  display_name: string | null;
  is_staff: boolean;
}

export type UserState = {
  currentUser: CurrentUser | null;
  isAuthenticated: boolean;
  setCurrentUser: (user: CurrentUser | null) => void;
  clearUser: () => void;
  updateUserProfile: (updates: Partial<Pick<CurrentUser, 'display_name'>>) => void;
  initializeUser: () => Promise<void>;
};

export const createUserSlice = (
  set: StoreApi<any>['setState'],
  get: StoreApi<any>['getState']
) => ({
  currentUser: null,
  isAuthenticated: false,
  setCurrentUser: (user: CurrentUser | null) =>
    set(() => ({
      currentUser: user,
      isAuthenticated: !!user,
    })),
  clearUser: () =>
    set(() => ({
      currentUser: null,
      isAuthenticated: false,
    })),
  updateUserProfile: (updates: Partial<Pick<CurrentUser, 'display_name'>>) =>
    set((state: UserState) => ({
      currentUser: state.currentUser ? { ...state.currentUser, ...updates } : null,
    })),
  initializeUser: async () => {
    try {
      const authUser = await fetchCurrentUser();

      if (!authUser) {
        get().clearUser();
        return;
      }

      const userData: CurrentUser = {
        id: authUser.id,
        email: authUser.email,
        display_name: authUser.display_name,
        is_staff: authUser.is_staff,
      };

      get().setCurrentUser(userData);
    } catch (error) {
      console.error('Error initializing user:', error);
      get().clearUser();
    }
  },
});
